import React, { useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import Scrollbars from 'react-custom-scrollbars';
import type { HotspotGroups } from '@types';
import { KRP_TARGET_ID } from '@constants';
import { $loadedScene } from '@stores/loadedStore';
import {
  $GateEditingGroup,
  $GateGroupStyle,
} from '@stores/editingHotspotStores';

type StyleData = Pick<
  HotspotGroups,
  'bgColor' | 'fontColor' | 'activeBgColor' | 'activeFontColor'
>;

export function HotspotGroupWidget() {
  const scene = useStore($loadedScene);
  const groupSetting = scene?.groups ?? {};
  const groupList = groupSetting.list ?? [];
  const editing = useStore($GateEditingGroup.state);
  const groupStyle = useStore($GateGroupStyle.state);
  const [listHeight, setListHeight] = useState(0);

  /**
   * 监听浏览器resize事件
   * 当浏览器窗口大小发生变化时，获取id为KRP_TARGET_ID的元素的高度
   * 根据不同的元素高度设置不同的列表显示的个数值
   * 屏幕高度<=400, 3.5
   * 400<屏幕高度<=800, 6.5
   * 800<屏幕高度<=900, 7.5
   * 900<屏幕高度<=1000, 8.5
   * 1000<屏幕高度<=1400, 9.5
   * 1400<屏幕高度<=1800, 11.5
   * 1800<屏幕高度<=2000, 13.5
   * 屏幕高度>2000, 15.5
   */
  useEffect(() => {
    let listNum = 0;
    const resize = () => {
      const containerHeight =
        document.getElementById(KRP_TARGET_ID)?.clientHeight ?? 0;
      if (containerHeight <= 400) {
        listNum = 3.5;
      } else if (containerHeight > 400 && containerHeight <= 800) {
        listNum = 6.5;
      } else if (containerHeight > 800 && containerHeight <= 900) {
        listNum = 7.5;
      } else if (containerHeight > 900 && containerHeight <= 1000) {
        listNum = 8.5;
      } else if (containerHeight > 1000 && containerHeight <= 1400) {
        listNum = 9.5;
      } else if (containerHeight > 1400 && containerHeight <= 1800) {
        listNum = 11.5;
      } else if (containerHeight > 1800 && containerHeight <= 2000) {
        listNum = 13.5;
      } else if (containerHeight > 2000) {
        listNum = 15.5;
      }
      setListHeight(listNum * 30 + Math.floor(listNum) * 5);
    };
    resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  if (!scene) return null;

  const maxHeight = groupList.length * (30 + 5) - 5;

  function getStyle<K extends keyof StyleData>(
    k: K,
    defaultValue: StyleData[K]
  ) {
    return groupStyle?.[k] ?? groupSetting[k] ?? defaultValue;
  }

  return (
    <Scrollbars
      style={{
        position: 'absolute',
        left: 10,
        zIndex: 2800,
        width: 90,
        height: Math.min(listHeight, maxHeight),
      }}
    >
      {groupList.map((item, i) => {
        const active = editing
          ? editing?.index === i && item.id !== 'more'
          : item.id === groupSetting.defaultId;
        const bgColor = active
          ? getStyle('activeBgColor', '250,100,0,1')
          : getStyle('bgColor', '0,0,0,0.5');
        const fontColor = active
          ? getStyle('activeFontColor', '255,255,255,1')
          : getStyle('fontColor', '255,255,255,1');
        return (
          <div
            key={item.id}
            className="mb-5 flex h-30 w-90 items-center justify-center rounded px-10 text-sm"
            style={{
              backgroundColor: `rgba(${bgColor})`,
              color: `rgba(${fontColor})`,
            }}
          >
            <div className="truncate">{item.name || '未命名'}</div>
          </div>
        );
      })}
    </Scrollbars>
  );
}
